import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useParams, Link as RouterLink, useLocation } from "react-router-dom";
import {
    Box,
    Typography,
    Breadcrumbs,
    Link,
    Chip,
    CircularProgress,
    Alert,
    Paper,
} from "@mui/material";
import { NavigateNext, Person } from "@mui/icons-material";
import { getUser } from "../../api/superAdminApi";
import { ADMIN_BASE_PATH } from "./adminNavConfig";
import { findServiceBySlug } from "./adminServiceConfig";
import { formatRoleLabel, parseUserRoles } from "../UserManagement/roleLabels";
import { WB_SX } from "../../ui/ux/uiTokens";

const AdminUserServiceLayout = () => {
    const { userId } = useParams();
    const { pathname } = useLocation();
    const serviceSlug = pathname.split("/").filter(Boolean).pop();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const service = findServiceBySlug(serviceSlug);

    const reloadUser = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getUser(userId);
            setUser(data);
        } catch (err) {
            setError(err.message || "Failed to load user");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        reloadUser();
    }, [reloadUser]);

    useEffect(() => {
        if (!user || !service) return;
        const prev = document.title;
        document.title = `${service.shortLabel} — ${user.name || `User #${userId}`} | Platform Admin`;
        return () => {
            document.title = prev;
        };
    }, [user, service, userId, service?.shortLabel]);

    if (!service) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Unknown service route.</Alert>
            </Box>
        );
    }

    const roleChips = parseUserRoles(user?.user_role);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: "auto" }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                <Link component={RouterLink} to={`${ADMIN_BASE_PATH}/all-users`} underline="hover" color="inherit">
                    All users
                </Link>
                <Typography color="text.primary">{user?.name || `User #${userId}`}</Typography>
                <Typography color="text.secondary">{service.label}</Typography>
            </Breadcrumbs>

            <Paper
                elevation={0}
                sx={{
                    ...WB_SX.card,
                    p: 2.5,
                    mb: 3,
                    borderLeft: `4px solid ${service.accent}`,
                    bgcolor: "white",
                }}
            >
                {loading ? (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: `${service.accent}18`,
                                color: service.accent,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Person />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="overline" color="text.secondary">
                                User #{user.id}
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {user.name || "Unnamed user"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {user.email}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {user.account_disabled && (
                                    <Chip label="Account disabled" size="small" color="error" variant="outlined" />
                                )}
                                {roleChips.map((r) => (
                                    <Chip key={r} label={formatRoleLabel(r)} size="small" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Managing
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: service.accent }}>
                                {service.label}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>

            {!loading && !error && user && (
                <Outlet context={{ user, userId, service, reloadUser }} />
            )}
        </Box>
    );
};

export default AdminUserServiceLayout;
